import TacticPlatform from '../models/TacticPlatform'

export const PLATFORM_NAME_EMPTY_MESSAGE = 'Please enter a platform name.'
export const PLATFORM_NAME_DUPLICATE_MESSAGE = 'A Platform with that name already exists.'

interface ApiViolation {
  propertyPath: string
  message?: string
}

interface ApiViolationResponse {
  response: {
    status: number
    data?: {
      violations?: ApiViolation[]
    }
  }
}

// Returns the validation message for a platform name, or null when it's valid.
// ignorePlatformIntId is the platform being renamed (0 when creating), so a case-only rename passes.
export function validatePlatformName(
  name: string,
  platforms: TacticPlatform[],
  ignorePlatformIntId: number
): string | null {
  if (name.trim() === '') {
    return PLATFORM_NAME_EMPTY_MESSAGE
  }
  const isDuplicate = platforms.some(
    (platform) =>
      platform.id.intID !== ignorePlatformIntId &&
      TacticPlatform.isSameName(platform.name, name)
  )
  return isDuplicate ? PLATFORM_NAME_DUPLICATE_MESSAGE : null
}

// Confirmation text for deleting a platform. tacticCount is null when the API doesn't send it.
export function platformDeleteMessage(tacticCount: number | null): string {
  if (tacticCount === null) {
    return 'Are you sure you want to delete this Platform? All associations with Tactics will be deleted along with it.'
  }
  if (tacticCount === 0) {
    return 'Are you sure you want to delete this Platform?'
  }
  return `This Platform is used by ${tacticCount} ${tacticCount === 1 ? 'Tactic' : 'Tactics'}. Deleting it will remove it from those Tactics and from every Tactic Type. This can't be undone.`
}

function isApiViolationResponse(error: unknown): error is ApiViolationResponse {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false
  }
  const response = (error as {response: unknown}).response
  return typeof response === 'object' && response !== null && 'status' in response
}

// True when the API rejected the request because the platform name is already used in the plan
export function isNameViolation(error: unknown): boolean {
  if (!isApiViolationResponse(error) || error.response.status !== 422) {
    return false
  }
  const violations = error.response.data?.violations ?? []
  return violations.some((violation) => violation.propertyPath === 'name')
}

interface PlanPlatformLists {
  tacticPlatforms: TacticPlatform[]
  channels: {tacticTypes: {tacticPlatforms: TacticPlatform[]}[]}[]
}

// Tactic Types hold their own platform objects (parsed from the channel payload), so a rename
// has to reach those copies as well as the plan's list.
export function renamePlatformInPlan(plan: PlanPlatformLists, platformIntId: number, newName: string): void {
  const rename = (platform: TacticPlatform) => {
    if (platform.id.intID === platformIntId) {
      platform.name = newName
    }
  }
  plan.tacticPlatforms.forEach(rename)
  plan.channels.forEach((channel) => {
    channel.tacticTypes.forEach((tacticType) => {
      tacticType.tacticPlatforms.forEach(rename)
    })
  })
}
